import * as React from 'react';
import { createPortal } from 'react-dom';
import {
  Tabs as BaseTabs,
  TabsContent,
  TabsList,
  TabsTrigger as BaseTabsTrigger,
} from '@erp/ui/tabs';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

/**
 * The element a `TabsBar` reserves for its actions, shared through the nearest
 * `Tabs` so a page rendered far below the bar (an `<Outlet />`) can put its own
 * action there with `TabsBarActions`.
 */
const TabsBarSlotContext = React.createContext<{
  slot: HTMLElement | null;
  setSlot: (element: HTMLElement | null) => void;
} | null>(null);

function Tabs(props: React.ComponentProps<typeof BaseTabs>) {
  const [slot, setSlot] = React.useState<HTMLElement | null>(null);
  return (
    <TabsBarSlotContext.Provider value={{ slot, setSlot }}>
      <BaseTabs {...props} />
    </TabsBarSlotContext.Provider>
  );
}

/**
 * A `TabsList` with room for actions on the same row. The bar, not the list,
 * draws the bottom border, so it runs the full width under the actions too.
 * When the tabs and actions don't fit on one row, the actions wrap onto their
 * own row above the tabs (`flex-wrap-reverse`) rather than overlapping them,
 * and the tabs stay seated on the border.
 */
function TabsBar({
  actions,
  className,
  children,
  ...props
}: React.ComponentProps<typeof TabsList> & { actions?: React.ReactNode }) {
  const setSlot = React.useContext(TabsBarSlotContext)?.setSlot;
  return (
    <div className="flex flex-wrap-reverse items-center gap-x-2 gap-y-1 border-b">
      <TabsList className={cn('w-auto border-b-0', className)} {...props}>
        {children}
      </TabsList>
      <div
        data-slot="tabs-bar-actions"
        className="ml-auto flex flex-wrap items-center justify-end gap-2"
      >
        <div ref={setSlot} className="flex items-center gap-2 empty:hidden" />
        {actions}
      </div>
    </div>
  );
}

/**
 * Render `children` in the enclosing `TabsBar`'s actions, from any depth.
 * Outside any `Tabs` (a page rendered on its own) they render in place.
 */
function TabsBarActions({ children }: { children: React.ReactNode }) {
  const context = React.useContext(TabsBarSlotContext);
  if (!context) return children;
  return context.slot ? createPortal(children, context.slot) : null;
}

/** A tab whose `description` explains it in a tooltip rather than on the page. */
function TabsTrigger({
  description,
  ...props
}: React.ComponentProps<typeof BaseTabsTrigger> & { description?: string }) {
  if (!description) return <BaseTabsTrigger {...props} />;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <BaseTabsTrigger {...props} />
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">{description}</TooltipContent>
    </Tooltip>
  );
}

export { Tabs, TabsBar, TabsBarActions, TabsContent, TabsList, TabsTrigger };
